// Typed fetch wrapper. Dev: API_BASE="" + vite proxy. Prod: nginx proxies /api.

export const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(`API ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

/** Human-readable message for any thrown error (FastAPI `{detail}` aware). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.detail;
    if (typeof d === "string" && d) return d;
    if (d && typeof d === "object" && "detail" in d) {
      const inner = (d as { detail: unknown }).detail;
      if (typeof inner === "string") return inner;
      if (Array.isArray(inner)) {
        // pydantic validation error array
        return inner
          .map((e) => (e && typeof e === "object" && "msg" in e ? String((e as { msg: unknown }).msg) : String(e)))
          .join("; ");
      }
    }
    return `Request failed (HTTP ${err.status}).`;
  }
  if (err instanceof Error) return err.message;
  return "Unexpected error.";
}

/** Absolute URL for asset <src> (video/rrd/thumbnail). */
export function assetUrl(path: string): string {
  return `${API_BASE}${path}`;
}

// Single-flight refresh: the Cognito ID token in the cookie lasts ~60 min; on the first 401
// every concurrent caller awaits ONE POST /api/auth/refresh (rotates the cookie) and retries
// once. If the refresh itself fails the original 401 propagates and the QueryCache handler
// in main.tsx invalidates ["me"] → redirect to /login.
let refreshInFlight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= fetch(`${API_BASE}/api/auth/refresh`, {
    method: "POST",
    credentials: "include",
  })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

async function rawFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    // hub auth: the Cognito ID token lives in an httpOnly cookie set by /api/auth/login;
    // include it on the cross-origin prod calls (console.gravando.ai → api.gravando.ai).
    // No-op same-origin.
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...(init?.headers ?? {}),
    },
  });
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res = await rawFetch(path, init);

  // auth endpoints are exempt: /login and /refresh must surface their own 401s
  if (res.status === 401 && !path.startsWith("/api/auth/")) {
    if (await refreshOnce()) res = await rawFetch(path, init);
  }

  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
